import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { isMaskedValue } from '../../../../../../../../utils/utils';
import {
    hasLinkedInEnrichment,
    isMaskedIdentityValue,
    MANAGEMENT_PROFILE_SAVED_TOAST,
    MANAGEMENT_PROFILE_FILL_ONLY_ROW_FIELDS,
    LINKEDIN_ROW_FIELD,
    MANAGEMENT_PROFILE_MASKABLE_FIELDS,
    MANAGEMENT_PROFILE_ROW_TO_UW_FIELD,
    triggersLinkedInScrape,
    triggersPanProfileEnrichment,
} from '../../../../../../../../common/ManagementProfile/managementProfileFields';
import { MANAGEMENT_PROFILE_FIELD_VALIDATORS } from '../../../../../../../../common/ManagementProfile/managementProfileFieldValidators';
import {
    EditableDirector,
    EditableDirectorField,
    EditableDirectorValues,
    UseEditDirectorDetailsModalProps,
} from '../ManagementUserProfile.types';

export const EDITABLE_DIRECTOR_FIELDS = Object.keys(MANAGEMENT_PROFILE_ROW_TO_UW_FIELD) as EditableDirectorField[];

const buildInitialValues = (director: EditableDirector): EditableDirectorValues =>
    EDITABLE_DIRECTOR_FIELDS.reduce((values, field) => {
        values[field] = director?.[field] ?? '';
        return values;
    }, {} as EditableDirectorValues);

export function useEditDirectorDetailsModal({
    director,
    onSave,
    onClose,
    linkedinRefreshing = false,
    panProfileFetching = false,
}: UseEditDirectorDetailsModalProps) {
    // Either enrichment holds the modal open; it closes when whichever one is running lands.
    const pendingEnrichment = linkedinRefreshing || panProfileFetching;
    const wasRefreshing = useRef(false);
    const savedAwaitingClose = useRef(false);

    const [initialValues, setInitialValues] = useState<EditableDirectorValues>(() => buildInitialValues(director));
    const [values, setValues] = useState<EditableDirectorValues>(initialValues);
    const [errors, setErrors] = useState<Partial<Record<EditableDirectorField, string>>>({});
    const [saving, setSaving] = useState(false);
    const [confirmingLinkedInClear, setConfirmingLinkedInClear] = useState(false);
    const [confirmingFillOnce, setConfirmingFillOnce] = useState(false);

    const closeAfterSave = useCallback(() => {
        if (savedAwaitingClose.current) {
            savedAwaitingClose.current = false;
            toast.success(MANAGEMENT_PROFILE_SAVED_TOAST);
        }
        onClose();
    }, [onClose]);

    useEffect(() => {
        if (wasRefreshing.current && !pendingEnrichment) closeAfterSave();
        wasRefreshing.current = pendingEnrichment;
    }, [pendingEnrichment, closeAfterSave]);

    const setField = (field: EditableDirectorField, value: string) => {
        setValues((current) => ({ ...current, [field]: value }));
        setErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));
    };

    // Only the fields being saved. Validating all five would block Save on a stored value the
    // user never touched — a legacy LinkedIn URL or a landline the API accepted before these
    // rules existed — with no way out except fixing an unrelated field first.
    const validateDirty = (dirty: Record<string, string>): boolean => {
        const nextErrors = Object.keys(dirty).reduce<Partial<Record<EditableDirectorField, string>>>((acc, field) => {
            const key = field as EditableDirectorField;
            const message = MANAGEMENT_PROFILE_FIELD_VALIDATORS[key]?.(values[key]);
            if (message) acc[key] = message;
            return acc;
        }, {});
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    /**
     * Which fill-only fields this director is missing. Captured from the values the modal opened
     * with, not from live state, so a field cannot flip to read-only under the user mid-edit.
     */
    const fillableIdentityFields = EDITABLE_DIRECTOR_FIELDS.filter(
        (field) =>
            MANAGEMENT_PROFILE_FILL_ONLY_ROW_FIELDS.has(field) &&
            (!initialValues[field]?.trim() || isMaskedIdentityValue(director?.[field as keyof typeof director] as string)),
    );

    /* 
       Restricted to the fields a source actually masks — "XXX Building" is a real address, not a
       mask. Mirrors underwriting's MANAGEMENT_PROFILE_MASKABLE_FIELDS. 
    */
    const isFieldMasked = (field: EditableDirectorField): boolean =>
        (MANAGEMENT_PROFILE_MASKABLE_FIELDS.has(field) || MANAGEMENT_PROFILE_FILL_ONLY_ROW_FIELDS.has(field)) &&
        isMaskedValue(initialValues[field]) &&
        values[field] === initialValues[field];

    const isFieldEditable = (field: EditableDirectorField): boolean =>
        !MANAGEMENT_PROFILE_FILL_ONLY_ROW_FIELDS.has(field) || fillableIdentityFields.includes(field);

    const dirtyUpdates = EDITABLE_DIRECTOR_FIELDS.reduce<Record<string, string>>((updates, field) => {
        if (!isFieldEditable(field)) return updates;
        const value = values[field].trim();
        if (value !== initialValues[field].trim()) updates[field] = value;
        return updates;
    }, {});

    const isSaveDisabled = saving || !Object.keys(dirtyUpdates).length || Object.values(errors).some(Boolean);

    /*
       Emptying the URL makes the backend drop the profile detail that URL produced, and that
       detail is not recoverable from the UI — so it needs consent, not just a save. Gated on
       enrichment actually existing: with nothing fetched there is nothing to warn about.
    */
    const clearsFetchedLinkedInData =
        LINKEDIN_ROW_FIELD in dirtyUpdates && !dirtyUpdates[LINKEDIN_ROW_FIELD] && hasLinkedInEnrichment(director?.customFields);

    const persist = async () => {
        setSaving(true);
        try {
            if (await onSave(dirtyUpdates)) {
                setInitialValues((current) => ({ ...current, ...dirtyUpdates }));
                savedAwaitingClose.current = true;
                // Held open only while an enrichment is pending, so the user sees it land and
                // understands why it takes a moment. A save that starts neither closes as usual.
                if (!triggersLinkedInScrape(dirtyUpdates) && !triggersPanProfileEnrichment(dirtyUpdates)) closeAfterSave();
            }
        } catch {
            // Never log the caught error — it can carry the request payload (phone/email/address).
            console.error('updateManagementProfile request failed');
            toast.error('Could not save. Please retry.');
        } finally {
            setSaving(false);
        }
    };

    /** Identity fields this save would fill — write-once, so the user is asked first. */
    const fillsIdentityFields = Object.keys(dirtyUpdates).filter((field) => MANAGEMENT_PROFILE_FILL_ONLY_ROW_FIELDS.has(field));

    const handleSave = async () => {
        if (isSaveDisabled || !validateDirty(dirtyUpdates)) return;
        if (clearsFetchedLinkedInData) {
            setConfirmingLinkedInClear(true);
            return;
        }
        if (fillsIdentityFields.length) {
            setConfirmingFillOnce(true);
            return;
        }
        await persist();
    };

    const confirmFillOnce = async () => {
        setConfirmingFillOnce(false);
        await persist();
    };

    const dismissFillOnce = () => setConfirmingFillOnce(false);

    /**
     * One save can both clear LinkedIn data and fill a write-once field. Each needs its own
     * consent — they are different irreversible acts — so the second popup opens where the first
     * would otherwise have persisted.
     */
    const confirmLinkedInClear = async () => {
        setConfirmingLinkedInClear(false);
        if (fillsIdentityFields.length) {
            setConfirmingFillOnce(true);
            return;
        }
        await persist();
    };

    /*
       Declining is a decision about the value, not just about the popup. The question asked was
       "delete the fetched data?", so answering no puts the URL back rather than leaving the field
       empty — an empty field after Cancel reads as though Cancel undid nothing, and the next Save
       of an unrelated field would clear LinkedIn anyway.
    */
    const dismissLinkedInClear = () => {
        setConfirmingLinkedInClear(false);
        setField(LINKEDIN_ROW_FIELD as EditableDirectorField, initialValues[LINKEDIN_ROW_FIELD as EditableDirectorField]);
    };

    return {
        values,
        errors,
        isFieldEditable,
        isFieldMasked,
        saving,
        isSaveDisabled,
        setField,
        handleSave,
        confirmingLinkedInClear,
        confirmLinkedInClear,
        dismissLinkedInClear,
        confirmingFillOnce,
        confirmFillOnce,
        dismissFillOnce,
    };
}
